import {getId, getType} from 'apis/model/base'
import React, {useCallback} from 'react'
import {BsFileEarmarkCheck, BsFileEarmarkRichtext, BsListUl, BsUiChecks} from 'react-icons/bs'
import {compose, fromRenderProps, nest, withProps} from 'recompose'
import {DelayRender} from 'views/Shared/components/DelayRender'
import {SettingsMenu} from 'views/AppLocals/factory/createSettingLayout'
import {BoxPlaceholder, renderOwnChild} from 'views/Shared'
import CustomSiderMenu from 'views/Shared/components/CustomSiderMenu'
import {Queries} from 'views/Shared/enums/Queries'
import {useMenuHandler} from 'views/Shared/handlers/useMenuHandler'
import WorkspaceContext from '../WorkspaceContext';
import ExaminationGroupReport from './ExaminationGroupReport'
import ExaminationPaperLists from './ExaminationPaperLists'
import ExaminationQuestionSets from './ExaminationQuestionSets'
import ExaminationRankingReport from './ExaminationRankingReport'
import ExaminationReportGroupByInfos from './ExaminationReportGroupByInfos'
import Examinations from './Examinations'

const ItemKeys = Object.freeze({
    REPORT: 'report',
    EXAMINATION: 'examination',
    EXAM_GROUP_REPORT: 'exam-group-report',
    EXAM_REPORT_GROUP_BY_INFOS: 'exam-report-group-by-infos',
    EXAMINATION_RECORDS: 'examination-records',
    EXAM_RANKING_REPORT: 'exam-ranking-report',
    EXAMINATION_QUESTIONS: 'examination-questions',
})

const menuItems = [
    {
        key: ItemKeys.EXAMINATION,
        label: 'examination',
        icon: <BsUiChecks size={16}/>,
    },
    {
        key: ItemKeys.EXAMINATION_RECORDS,
        label: 'examination records',
        icon: <BsFileEarmarkCheck size={16}/>,
    },
    {
        key: ItemKeys.EXAMINATION_QUESTIONS,
        label: 'questions set',
        icon: <BsFileEarmarkRichtext size={16}/>,
    },
    {
        key: ItemKeys.REPORT,
        label: 'report',
        icon: <BsListUl size={16}/>,
        children: [
            {
                key: ItemKeys.EXAM_RANKING_REPORT,
                label: 'exam ranking report',
            },
            {
                key: ItemKeys.EXAM_REPORT_GROUP_BY_INFOS,
                label: 'exam report group by info',
            },
        ],
    },
]

const routeEntities = [
    {
        key: ItemKeys.EXAMINATION,
        component: Examinations,
    },
    {
        key: ItemKeys.EXAMINATION_RECORDS,
        component: ExaminationPaperLists,
    },
    {
        key: ItemKeys.EXAMINATION_QUESTIONS,
        component: ExaminationQuestionSets,
    },
    {
        key: ItemKeys.EXAM_RANKING_REPORT,
        component: ExaminationRankingReport,
    },
    {
        key: ItemKeys.EXAM_GROUP_REPORT,
        component: ExaminationGroupReport,
    },
    {
        key: ItemKeys.EXAM_REPORT_GROUP_BY_INFOS,
        component: ExaminationReportGroupByInfos,
    },
    {
        key: ItemKeys.REPORT,
        component: () => <BoxPlaceholder title={ItemKeys.REPORT}/>,
    },
].map(({key, ...rest}) => ({
    key,
    path: key,
    ...rest,
}))

const WorkspaceExamination = ({owner_id, owner_type}) => {
    const {activeKey, Component, onMenuSelect} = useMenuHandler({
        routes: routeEntities,
        actionName: Queries.REL,
    })

    const renderMenu = useCallback(
        () => (
            <CustomSiderMenu
                deps={[activeKey]}
                Wrapper={renderOwnChild}>
                <SettingsMenu
                    items={menuItems}
                    onSelect={onMenuSelect}
                    defaultOpenKeys={[ItemKeys.REPORT]}
                    defaultSelectedKeys={[activeKey]}
                />
            </CustomSiderMenu>
        ),
        [menuItems, activeKey]
    )

    return (
        <div className="px-3 md:px-6">
            <Component {...{owner_id, owner_type}} />
        </div>
    )
}

export default nest(
    withProps({lazy: true})(DelayRender),
    compose(
        fromRenderProps(WorkspaceContext.Consumer, ({organization}) => ({
            owner_id: getId(organization),
            owner_type: getType(organization),
        }))
    )(WorkspaceExamination)
)
